/**
 * Features related to interacting with a node information for a user
 * such as resume position, playlist status, etc.
 * @module serives/user-node-info
 */
import { get as apiGet, post as apiPost, TYPE_BROOKLYN } from 'api-client'
import strictUriEncode from 'strict-uri-encode'
import { map as mapPromise } from 'bluebird'
import _assign from 'lodash/assign'
import _find from 'lodash/find'
import _cloneDeep from 'lodash/cloneDeep'
import _get from 'lodash/get'
import _isArray from 'lodash/isArray'
import _join from 'lodash/join'
import _map from 'lodash/map'
import _parseInt from 'lodash/parseInt'
import _partial from 'lodash/partial'
import _size from 'lodash/size'
import _reduce from 'lodash/reduce'
import _compact from 'lodash/compact'
import { get as getMediaData } from 'services/media'

export async function getMany (nodeIds, options = {}) {
  const { auth } = options
  const requestOptions = {
    path: 'api/user/node', // This path still requires the api/ path
    params: nodeIds.join(','),
  }
  const response = await apiPost('batchget', requestOptions, { auth }, TYPE_BROOKLYN)
  const batchResponses = _get(response, 'body.batchResponses', [])
  // Return an object with data keyed by node id
  return batchResponses.reduce((acc, item) => {
    const id = _get(item, 'id')
    if (!id) {
      return acc
    }

    // Convert to expected data types
    const itemData = _get(item, 'data', {})
    acc[item.id] = convertTypes(itemData)
    return acc
  }, {})
}

export async function getOne (nodeId, options = {}) {
  const { auth } = options
  const response = await apiGet(`user/node/${nodeId}`, {}, { auth }, TYPE_BROOKLYN)
  const body = _get(response, 'body', {})

  // Convert to expected data types
  return convertTypes(body)
}

function convertTypes (data) {
  return {
    playlist: data.playlist || false,
    featurePosition: data.featurePosition ? Number(data.featurePosition) : 0,
  }
}

// when we are getting series nodes, we also need to grab the featured episode info now
// so that we can show the continue watching episode (all series tiles should going forward)
// IMO this should be refactored to happen in brooklyn and come back with the batchget req
// but apps is already using this secondary endpoint so we are going with this implementation
// for now
export async function getFeaturedEpisodeData (nodeIds, options = {}) {
  const { auth, user } = options
  const language = user.getIn(['data', 'language'])
  const filteredNodeIds = _compact(nodeIds)

  // Fetch all node data concurrently
  const responses = await mapPromise(filteredNodeIds, async (nodeId) => {
    try {
      const data = await apiGet(
        `v5/videos/series/${strictUriEncode(nodeId)}/featured`,
        { language },
        { auth },
        TYPE_BROOKLYN,
      )
      const featuredVideo = _get(data, 'body.featuredVideo', {})
      const body = {
        mediaId: _get(featuredVideo, ['feature', 'mediaId'], null),
        epidodeCount: _get(featuredVideo, 'episodeCount', null),
        feature: _get(featuredVideo, 'feature', {}) || {},
        preview: _get(featuredVideo, 'preview', {}) || {},
        episode: _get(featuredVideo, 'episode', null),
        season: _get(featuredVideo, 'season', null),
        contentId: _get(featuredVideo, 'id', null),
        title: _get(featuredVideo, 'title', null),
        teaser: _get(featuredVideo, 'teaser', ''),
        path: _get(featuredVideo, 'path', ''),
        type: _get(featuredVideo, 'type', {}),
      }
      return { nodeId, body }
    } catch (error) {
      return { nodeId, error }
    }
  })
  const mediaIds = _compact(_map(responses, (response) => {
    if (response.body) {
      return response.body.mediaId
    }
    return undefined
  }))
  // Fetch all media data concurrently
  const mediaResponses = await mapPromise(mediaIds, async (mediaId) => {
    try {
      const data = await getMediaData({ id: mediaId, auth, user })
      const media = {
        textTracks: _get(data, 'textTracks', {}) || {},
        mediaLang: _get(data, 'mediaLang', ''),
        byLang: _get(data, 'byLang', {}) || {},
      }
      return { mediaId, media }
    } catch (error) {
      return { mediaId, error }
    }
  })
  // Return { error } or { data } keyed by nodeId
  const featuredEpisodes = _reduce(responses, (acc, response) => {
    const { nodeId, error, body } = response
    if (error) {
      acc[nodeId] = { error }
    } else if (body) {
      // merge in media items
      const mediaData = _find(mediaResponses, (media) => media.mediaId === body.mediaId)
      acc[nodeId] = _assign({ data: body }, { media: mediaData.media })
    }
    return acc
  }, {})
  return featuredEpisodes
}

// Legacy code below here
const SCHEMA_USER = {
  id: null,
  _dataError: null,
  featurePosition: 0,
  playlist: false,
}

/**
 * Get user node info using batchget endpoint
 * @param {Object} options The options
 * @param {Number[]} options.ids A list of content ids
 * @param {import('immutable').Map|String} [options.auth] Optional authentication
 */
export async function legacyBatchGet (options = {}) {
  const { ids = [], auth } = options
  if (!ids || _size(ids) === 0) {
    return handleGetResponse({}, false)
  }
  const params = {
    path: 'api/user/node', // This path still requires the api/ path
    params: _join(ids, ','),
  }
  try {
    const res = await apiPost('batchget', params, { auth }, TYPE_BROOKLYN)
    return handleGetResponse(res)
  } catch (e) {
    return handleGetResponse({}, true)
  }
}

export function handleGetResponse (res, _dataError) {
  const batchResponses = _get(res, 'body.batchResponses', [])
  const rows = _isArray(batchResponses) ? batchResponses : []
  return _map(rows, _partial(createModel, _partial.placeholder, _dataError))
}

export function createModel (data, _dataError) {
  const featurePosition = _get(data, 'data.featurePosition', 0)
  return _assign(_cloneDeep(SCHEMA_USER), {
    id: _parseInt(_get(data, 'id', -1)),
    _dataError,
    featurePosition: featurePosition ? _parseInt(featurePosition) : 0,
    playlist: _get(data, 'data.playlist', false),
  })
}
